/**
 * Блок «Как ссылаться на файлы» в справке модели («?») — из общего определения переменных
 * (prompt-vars): что такое пилюли, как их зовут, что получит модель и пример роли для
 * каждого канала. Владелец 25.09: «чем лучше научим клиентов пользоваться пилюлями, тем
 * лучше результат». Модель без входов-референсов блока не получает.
 *
 * Пилюля в справке зовётся так, как юзер видит её в редакторе: у модели со своим
 * синтаксисом это токен провайдера (@Image1, @Video1), у остальных — подпись из спеки
 * (Image 1, «Видео»). Иначе справка учит одному имени, а редактор показывает другое.
 */
import { promptRefStyleOf, providerToken, varLabel, varPhrase } from '../prompt-vars'
import type { Model } from '../data/models'

export interface PromptRefsHelp {
	items: { chip: string; text: string }[]
	example: string
	note: string
}

type Channel = 'image' | 'video' | 'audio'

export function promptRefsHelpFor(model: Pick<Model, 'id' | 'backendModel' | 'provider' | 'maxImages' | 'supportsReferenceImages' | 'supportsReferenceVideo' | 'supportsReferenceAudio' | 'promptRefStyle'> | null | undefined): PromptRefsHelp | null {
	if (!model) return null
	const photos = (model.maxImages ?? 1) > 1 || !!model.supportsReferenceImages
	const video = !!model.supportsReferenceVideo
	const audio = !!model.supportsReferenceAudio
	if (!photos && !video && !audio) return null
	const style = promptRefStyleOf(model)
	const token = (channel: Channel, n = 1) => providerToken(channel, n, style)
	/** Имя пилюли, как в редакторе. */
	const label = (channel: Channel, n = 1) => token(channel, n) ?? (channel === 'image' ? varLabel('photo', 'image', n) : varLabel(channel))
	/** То же имя внутри фразы: слово-подпись («Видео») берём в кавычки, токен и Image 1 — как есть. */
	const inText = (channel: Channel, n = 1) => (token(channel, n) || channel === 'image' ? label(channel, n) : `«${label(channel)}»`)
	const items: { chip: string; text: string }[] = []
	const example: string[] = []
	if (photos) {
		items.push({
			chip: `${label('image', 1)}, ${label('image', 2)}…`,
			text: `Фото по порядку загрузки: пилюля ${label('image', 1)} — первое фото, ${label('image', 2)} — второе${style ? ', модель получает их именно так' : ''}. Напишите, что из него взять: внешность, одежду, предмет или фон.`,
		})
		example.push(`внешность из ${inText('image', 1)}`)
	}
	if (video) {
		items.push({ chip: label('video'), text: `Видео-референс: пилюля ${inText('video')}${style ? '' : ` (в тексте — ${varPhrase('video')})`}. Обычно из него берут движение, ритм и камеру.` })
		example.push(`движение из ${inText('video')}`)
	}
	if (audio) {
		items.push({ chip: label('audio'), text: `Аудио-референс: пилюля ${inText('audio')}${style ? '' : ` (в тексте — ${varPhrase('audio')})`}. Звук, музыка или озвучка ролика.` })
		example.push(`звук из ${inText('audio')}`)
	}
	return {
		items,
		example: `Пример: «${example.join(', ')}».`,
		note: 'Файл, который в промпте не упомянут, модель использует по своему усмотрению — чипы «Сослаться на фото» под промптом вставляют ссылку туда, где стоит курсор.',
	}
}
